import { resolveCountryCode } from "../shared/constants/countries";
import { resolveIndianStateCode } from "../shared/constants/indianStates";

// Utility exports
export * from "./validation";

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const getRelativeTime = (dateString: string): string => {
  const now = new Date();
  let date: Date;

  try {
    // Handle both MySQL datetime format ("2025-09-22 11:31:02") and ISO format
    // If it's already ISO format (contains 'T'), use it directly
    // Otherwise, convert MySQL format to ISO
    let isoString = dateString;
    if (!dateString.includes("T")) {
      // MySQL format - convert to ISO
      isoString = dateString.replace(" ", "T") + "Z";
    }

    date = new Date(isoString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid date string:", dateString);
      return "Unknown time";
    }
  } catch (error) {
    console.warn("Error parsing date:", dateString, error);
    return "Unknown time";
  }

  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInMinutes < 1) {
    return "Just now";
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? "" : "s"} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
  } else {
    return formatDate(dateString);
  }
};

// State name → Zoho GST state/UT code (delegates to shared indianStates constants)
export const getStateAbbreviation = (
  stateName: string | null | undefined,
): string => resolveIndianStateCode(stateName ?? "");

// Country name/code → country code (delegates to shared countries constants)
export const getCountryAbbreviation = (
  countryName: string | null | undefined,
): string => resolveCountryCode(countryName ?? "");

/**
 * Extracts the last leaf from an account name that may contain hierarchy separators (>).
 * For example: "GST Payable > Output GST" returns "Output GST"
 * If the name doesn't contain ">", returns the name as-is.
 *
 * @param accountName - The account name that may contain hierarchy separators
 * @returns The last leaf of the account name after ">" or the original name
 */
export const getAccountNameLeaf = (
  accountName: string | null | undefined
): string => {
  if (!accountName) {
    return "";
  }

  if (accountName.includes(">")) {
    const parts = accountName.split(">");
    return parts[parts.length - 1].trim();
  }

  return accountName;
};

// Re-export all utilities for easier imports
export * as validation from "./validation";
